import React from 'react';
import styled from 'libs/styled';
import type { MediaItemComponent } from '@fogia/core-web/components/SanityMedia/SanityBackgroundMedia';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { useSanityImage } from './useSanityImage';
import { getAltTextFromReferences, getLqipFromReferences } from './utils';

/**
 * Props for the SanityBackgroundImage component
 *
 * @interface SanityBackgroundImageProps
 */
export interface SanityBackgroundImageProps {
    /** Sanity image object with asset reference, crop, and hotspot data */
    image: NonNullable<MediaItemComponent['image']>;
    /** Alt text for the image. Defaults to empty string for decorative background images */
    alt?: string;
    /** CSS object-fit value. Defaults to 'cover' */
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    /** Loading strategy. 'lazy' is default */
    loading?: 'lazy' | 'eager' | 'auto';
    /** Widths (px) for Sanity CDN srcSet. Defaults to SANITY_IMAGE_SRCSET_WIDTHS when omitted. */
    srcSetWidths?: readonly number[];
    /** sizes attribute for <img> (e.g. "100vw" or "(min-width: 48em) 50vw, 100vw"). */
    sizes?: string;
    /** Optional width for the <img> element (layout/intrinsic size). */
    width?: string | number;
    /** Optional height for the <img> element (layout/intrinsic size). */
    height?: string | number;
    /** React children to render on top of the image */
    children?: React.ReactNode;
    /** Placeholder image URL or boolean. If true, generates a small blurred version automatically */
    placeholder?: string | boolean;
    /** Callback fired when the image has loaded */
    onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

const MediaWrapper = styled('div')`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

const PlaceholderWrapper = styled('div')`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    z-index: 0;
`;

const StyledImage = styled('img')`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
    z-index: 0;
    will-change: opacity;
`;

const ContentWrapper = styled('div')`
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
`;

/**
 * Component for rendering Sanity images as backgrounds with content overlay support.
 *
 * This component renders a semantic `<img>` element (instead of CSS background-image) for better accessibility,
 * while positioning it absolutely to function as a background. It supports:
 * - Lazy loading with IntersectionObserver for better performance
 * - Placeholder images (small blurred versions) while loading
 * - Sanity's crop and hotspot features for optimal image positioning
 * - Children overlay support (content rendered on top of the image)
 * - Fallback via `<noscript>` tag for users without JavaScript
 *
 * The image is positioned absolutely with z-index 0, and children are wrapped in a ContentWrapper with z-index 1
 * to ensure proper layering.
 *
 * @example
 * ```tsx
 * <SanityBackgroundImage
 *   image={sanityImageObject}
 *   alt=""
 *   objectFit="cover"
 *   loading="lazy"
 * >
 *   <h1>Content over image</h1>
 * </SanityBackgroundImage>
 * ```
 *
 * @returns Rendered image component or null if image URL cannot be generated
 */
const SanityBackgroundImage: React.FC<SanityBackgroundImageProps> = ({
    image,
    alt,
    objectFit = 'cover',
    loading = 'lazy',
    srcSetWidths,
    sizes,
    width,
    height,
    children,
    placeholder,
    onLoad,
    ...rest
}) => {
    const pageReferences = (useAppSelector((state) => state.page?.data?.references) ?? {}) as Record<string, unknown>;
    const resolvedAlt = alt ?? getAltTextFromReferences(image, pageReferences) ?? '';
    const placeholderFromRef = getLqipFromReferences(image, pageReferences);

    // Use shared lazy loading hook for IntersectionObserver, placeholder generation,
    // image URL generation, and object position calculation
    const {
        imageUrl,
        srcSet,
        placeholderUrl,
        objectPosition,
        imgProps,
        criticalStyle,
        handleLoad,
        isVisible,
        isCritical,
        shouldShowPlaceholder,
    } = useSanityImage({
        image,
        loading,
        srcSetWidths,
        sizes,
        placeholder: placeholderFromRef ?? placeholder ?? true,
        onLoad,
    });

    if (!imageUrl) {
        return null;
    }

    return (
        <MediaWrapper {...rest}>
            {shouldShowPlaceholder && (
                <PlaceholderWrapper
                    backgroundImage={`url(${placeholderUrl || ''})`}
                    backgroundSize={objectFit || 'cover'}
                    backgroundPosition={objectPosition || 'center center'}
                />
            )}
            {isVisible && (
                <StyledImage
                    alt={resolvedAlt}
                    loading={loading}
                    objectFit={objectFit}
                    objectPosition={objectPosition}
                    width={width}
                    height={height}
                    fetchpriority={loading === 'eager' ? 'high' : 'auto'}
                    {...imgProps}
                    style={criticalStyle}
                    onLoad={handleLoad}
                />
            )}
            {!isCritical && (
                <noscript>
                    <StyledImage
                        src={imageUrl}
                        srcSet={srcSet ?? undefined}
                        sizes={sizes}
                        alt={resolvedAlt}
                        objectFit={objectFit}
                        objectPosition={objectPosition}
                        width={width}
                        height={height}
                        style={{ opacity: 1 }}
                    />
                </noscript>
            )}
            {children && <ContentWrapper>{children}</ContentWrapper>}
        </MediaWrapper>
    );
};

export default SanityBackgroundImage;
